import {
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Collapse,
  FileButton,
  Group,
  Image,
  MultiSelect,
  NumberInput,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, Navigate } from '@tanstack/react-router';
import { VIBE_TAGS } from '@wwaave/shared/vocab';
import { type ReactNode, useRef, useState } from 'react';

import { AddressLookup } from '../../../components/AddressLookup';
import { AppShell } from '../../../components/app-shell/AppShell';
import { VENUE_FORM_DEFAULTS } from '../../../components/venue/defaults';
import type { VenueFormValues } from '../../../components/venue/types';
import {
  VenueAmenitiesFields,
  VenueExtrasFields,
  VenueStipendFields,
} from '../../../components/venue/VenueFormFields';
import { JourneyStrip } from '../../../components/wizard/JourneyStrip';
import { type ChecklistItem, NextStepsChecklist } from '../../../components/wizard/NextStepsChecklist';
import {
  artFormNames,
  countryOptions,
  groupedArtFormOptions,
  groupedVenueTypeOptions,
  venueTypeNames,
} from '../../../components/wizard/vocab-options';
import { Wizard, type WizardStep } from '../../../components/wizard/Wizard';
import { ApiError, mutateJson } from '../../../lib/api';
import {
  fetchHostVerifications,
  summariseHostVerificationState,
} from '../../../lib/host-verification';
import { track } from '../../../lib/telemetry';
import { uploadImage } from '../../../lib/upload';
import { useVenueBillingStatus } from '../../../lib/venue-subscription';

/**
 * /host/venues/new — venue setup as a one-question-per-screen wizard.
 *
 * Details → Check it over → (draft created) → Photos → What's left. The draft
 * is created on the review step, so everything before it is free to go back
 * and forth over; after it, Back is locked (going back would create a second
 * venue) and the remaining edits live on the venue editor.
 *
 * The closing screen mirrors the server's go-live preconditions
 * (checkLivenessPreconditions in worker routes/venues-edit.ts): a hero photo,
 * an art form, a venue type, real coordinates, and an approved identity
 * verification. When they're all met it offers Submit for review right there.
 */

type FormValues = VenueFormValues & { slug: string };

const INITIAL_VALUES: FormValues = {
  ...VENUE_FORM_DEFAULTS,
  slug: '',
  country: 'AU',
  timezone: 'Australia/Brisbane',
};

type CreateVenueResponse = { venue: { id: string; slug: string } };
type UploadedPhoto = { id: string; url: string; alt_text: string; is_hero: boolean };

const SLUG_RE = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;
const PHOTO_ACCEPT = 'image/jpeg,image/png,image/webp,image/avif,image/heic,image/heif';

function VenueNew() {
  const billing = useVenueBillingStatus();
  const qc = useQueryClient();
  const [step, setStep] = useState(0);
  const [venue, setVenue] = useState<CreateVenueResponse['venue'] | null>(null);
  const [photos, setPhotos] = useState<UploadedPhoto[]>([]);
  const [editSlug, setEditSlug] = useState(false);
  const [manualAddress, setManualAddress] = useState(false);
  // While untouched, typing the name derives the slug; once edited by hand it stays.
  const slugManuallyEdited = useRef(false);

  const verificationQ = useQuery({
    queryKey: ['verifications', 'me'],
    queryFn: fetchHostVerifications,
  });
  const verificationState =
    verificationQ.data !== undefined && verificationQ.data.ok
      ? summariseHostVerificationState(verificationQ.data.verifications)
      : 'none';

  const form = useForm<FormValues>({
    initialValues: INITIAL_VALUES,
    validate: {
      name: (v) => (v.trim().length < 2 ? 'Add the venue’s name.' : null),
      slug: (v) =>
        SLUG_RE.test(v) ? null : 'Lowercase letters, numbers and hyphens, no hyphen at either end.',
      description: (v) => (v.trim().length < 20 ? 'Write at least a sentence or two.' : null),
      venue_types: (v) =>
        v.length === 0 ? 'Pick at least one.' : v.length > 4 ? 'Pick up to four.' : null,
      art_forms_accepted: (v) => (v.length === 0 ? 'Pick at least one art form you welcome.' : null),
      capacity_min: (v) => (typeof v !== 'number' || v < 1 ? 'How few people is still a good night?' : null),
      capacity_max: (v, all) =>
        typeof v !== 'number' || v < 1
          ? 'What’s the most the room holds?'
          : typeof all.capacity_min === 'number' && v < all.capacity_min
            ? 'This needs to be at least the smallest crowd.'
            : null,
      address_line: (v) => (v.trim().length < 2 ? 'Add the street address.' : null),
      city: (v) => (v.trim().length === 0 ? 'Add the city or town.' : null),
      country: (v) => (v.length !== 2 ? 'Pick a country.' : null),
      // CFR-010: 0,0 is never a real venue, and blocks publishing server-side.
      lat: (v, all) =>
        typeof v !== 'number' || typeof all.lng !== 'number' || (v === 0 && all.lng === 0)
          ? 'Pick your address from the search so we can place it on the map.'
          : null,
      // CFR-004: a vague "stipend available" line forces the artist to message
      // just to learn whether it covers fuel.
      stipend_description: (v, all) =>
        all.stipend_offered && v.trim().length < 10
          ? 'Tell artists what the stipend covers. At least a sentence.'
          : null,
    },
  });
  const v = form.values;

  function valid(fields: (keyof FormValues)[]): boolean {
    let ok = true;
    for (const f of fields) if (form.validateField(f).hasError) ok = false;
    return ok;
  }

  const create = async () => {
    const all = form.validate();
    if (all.hasErrors) {
      const firstBad = DETAIL_STEP_FIELDS.findIndex((fields) =>
        fields.some((f) => all.errors[f] !== undefined),
      );
      if (firstBad >= 0) setStep(firstBad);
      return false;
    }
    try {
      const res = await mutateJson<CreateVenueResponse>('/api/venues/me/venues', toCreatePayload(v));
      setVenue(res.venue);
      void qc.invalidateQueries({ queryKey: ['host', 'venues'] });
    } catch (err) {
      // 402: payments are on and there's no live subscription (another tab
      // cancelled, say). The subscribe step is where that gets fixed.
      if (err instanceof ApiError && err.status === 402) {
        void qc.invalidateQueries({ queryKey: ['billing', 'me'] });
      }
      // 409 slug clash: send them to the name step with the slug open.
      if (err instanceof ApiError && err.status === 409) {
        setEditSlug(true);
        setStep(0);
        form.setFieldError(
          'slug',
          'Another venue already has this page address. Try adding your suburb, e.g. the-shed-paddington.',
        );
        return false;
      }
      throw err;
    }
  };

  // Venues subscribe during sign-up; a host who skipped that step goes back to
  // it (the server's 402 on create is the real gate, this saves a wasted form).
  if (billing.data?.subscription_required === true && venue === null) {
    return <Navigate to="/onboarding/host" replace />;
  }

  const hero = photos.find((p) => p.is_hero) ?? photos[0];
  const identityApproved = verificationState === 'approved';

  const steps: WizardStep[] = [
    {
      id: 'name',
      label: 'Name',
      title: 'What’s the venue called?',
      lede: 'The name artists will see when they browse.',
      onNext: () => valid(['name', 'slug']),
      body: (
        <Stack gap="xs">
          <TextInput
            data-autofocus
            size="lg"
            aria-label="Venue name"
            placeholder="e.g. The Shed"
            {...form.getInputProps('name')}
            onChange={(e) => {
              form.getInputProps('name').onChange(e);
              if (!slugManuallyEdited.current) form.setFieldValue('slug', slugify(e.currentTarget.value));
            }}
          />
          <Text size="sm" c="dimmed">
            Its page will live at{' '}
            <Text span ff="monospace" size="sm">
              wwaave.com/venues/{v.slug.length > 0 ? v.slug : '…'}
            </Text>{' '}
            {!editSlug ? (
              <Anchor component="button" type="button" size="sm" onClick={() => setEditSlug(true)}>
                Change
              </Anchor>
            ) : null}
          </Text>
          <Collapse expanded={editSlug || form.errors['slug'] !== undefined}>
            <TextInput
              label="Page address"
              description="Lowercase letters, numbers and hyphens."
              {...form.getInputProps('slug')}
              onChange={(e) => {
                slugManuallyEdited.current = true;
                form.getInputProps('slug').onChange(e);
              }}
            />
          </Collapse>
        </Stack>
      ),
    },
    {
      id: 'room',
      label: 'The room',
      title: 'Tell artists about the room.',
      lede: 'What kind of space is it, who comes, what’s a good night like?',
      onNext: () => valid(['summary', 'description']),
      body: (
        <Stack gap="md">
          <TextInput
            data-autofocus
            size="md"
            label="In one line"
            description="Optional. Shown under the name on browse cards."
            placeholder="A converted woolshed with a warm, listening crowd"
            maxLength={160}
            {...form.getInputProps('summary')}
          />
          <Textarea
            size="md"
            label="The full picture"
            autosize
            minRows={5}
            maxRows={16}
            {...form.getInputProps('description')}
          />
        </Stack>
      ),
    },
    {
      id: 'type',
      label: 'Type',
      title: 'What kind of place is it, and who’s welcome?',
      lede: 'Artists filter by both. Pick everything that fits.',
      onNext: () => valid(['venue_types', 'art_forms_accepted']),
      body: (
        <Stack gap="md">
          <MultiSelect
            data-autofocus
            size="md"
            label="Venue type"
            description="Up to four."
            data={groupedVenueTypeOptions()}
            searchable
            hidePickedOptions
            maxValues={4}
            maxDropdownHeight={260}
            {...form.getInputProps('venue_types')}
          />
          <MultiSelect
            size="md"
            label="Art forms you welcome"
            data={groupedArtFormOptions()}
            searchable
            hidePickedOptions
            maxDropdownHeight={260}
            {...form.getInputProps('art_forms_accepted')}
          />
          <MultiSelect
            size="md"
            label="Vibe"
            description="Optional. Up to eight."
            data={VIBE_TAGS.map((t) => ({ value: t.slug, label: t.name }))}
            searchable
            hidePickedOptions
            maxValues={8}
            {...form.getInputProps('vibe_tags')}
          />
        </Stack>
      ),
    },
    {
      id: 'where',
      label: 'Address',
      title: 'Where is it?',
      lede: 'Search for the address. Artists only see the street address once you confirm a stay, unless you choose otherwise below.',
      onNext: () => valid(['address_line', 'city', 'country', 'lat']),
      body: (
        <Stack gap="md">
          <AddressLookup
            label="Search for the address"
            initialQuery={[v.address_line, v.city].filter((s) => s.length > 0).join(', ')}
            onSelect={(m) => {
              track('host.address_lookup_used', { selected: true });
              form.setValues({
                address_line: m.addressLine,
                city: m.city,
                region: m.region,
                postcode: m.postcode,
                country: m.country,
                lat: m.lat,
                lng: m.lng,
              });
              form.clearFieldError('lat');
            }}
          />
          {typeof v.lat === 'number' && typeof v.lng === 'number' && !(v.lat === 0 && v.lng === 0) ? (
            <Paper withBorder radius="md" p="sm">
              <Text size="sm" fw={600}>
                {v.address_line}
              </Text>
              <Text size="sm" c="dimmed">
                {[v.city, v.region, v.postcode].filter((s) => s.length > 0).join(' ')}
              </Text>
            </Paper>
          ) : null}
          {form.errors['lat'] !== undefined && !manualAddress ? (
            <Text size="sm" c="red">
              {form.errors['lat']}
            </Text>
          ) : null}
          <Anchor component="button" type="button" size="sm" onClick={() => setManualAddress((o) => !o)}>
            {manualAddress ? 'Hide the address fields' : 'Can’t find it? Enter it by hand'}
          </Anchor>
          <Collapse expanded={manualAddress}>
            <Stack gap="sm">
              <TextInput label="Street address" {...form.getInputProps('address_line')} />
              <Group grow wrap="wrap">
                <TextInput label="City or town" {...form.getInputProps('city')} />
                <TextInput label="State" {...form.getInputProps('region')} />
                <TextInput label="Postcode" {...form.getInputProps('postcode')} />
              </Group>
              <Select
                label="Country"
                data={countryOptions()}
                searchable
                allowDeselect={false}
                {...form.getInputProps('country')}
              />
              <Group grow>
                <NumberInput
                  label="Latitude"
                  min={-90}
                  max={90}
                  decimalScale={6}
                  {...form.getInputProps('lat')}
                />
                <NumberInput
                  label="Longitude"
                  min={-180}
                  max={180}
                  decimalScale={6}
                  {...form.getInputProps('lng')}
                />
              </Group>
              <Text size="xs" c="dimmed">
                Map coordinates place your venue on the browse map. Search above fills them in; by
                hand, copy them from a map app.
              </Text>
            </Stack>
          </Collapse>
          <Switch
            label="Show the full street address publicly"
            description="Off by default. It’s shared automatically once you confirm a stay."
            {...form.getInputProps('address_visible_to_public', { type: 'checkbox' })}
          />
        </Stack>
      ),
    },
    {
      id: 'capacity',
      label: 'Capacity',
      title: 'How big a crowd suits the room?',
      lede: 'A range is fine: the smallest crowd that still feels good, and the most it holds.',
      onNext: () => valid(['capacity_min', 'capacity_max']),
      body: (
        <Group grow align="flex-start">
          <NumberInput
            data-autofocus
            size="md"
            label="At least"
            min={1}
            max={5000}
            {...form.getInputProps('capacity_min')}
          />
          <NumberInput size="md" label="At most" min={1} max={5000} {...form.getInputProps('capacity_max')} />
        </Group>
      ),
    },
    {
      id: 'offer',
      label: 'On offer',
      title: 'What’s on offer?',
      lede: 'Sound gear, where artists sleep, food and drink. The more you say, the fewer questions you’ll get.',
      optional: true,
      body: <VenueAmenitiesFields form={form} bare />,
    },
    {
      id: 'extras',
      label: 'Rules and extras',
      title: 'House rules, and anything extra.',
      lede: 'Quiet hours, pets, a stipend, fuel money, recording time. Be plain about money: hosts who are, get better requests.',
      optional: true,
      onNext: () => valid(['stipend_description']),
      body: (
        <Stack gap="xl">
          <VenueStipendFields form={form} bare />
          <VenueExtrasFields form={form} bare />
        </Stack>
      ),
    },
    {
      id: 'review',
      label: 'Check it over',
      title: 'Check it over.',
      lede: 'This saves your venue as a draft. Nothing is public until our team reviews it.',
      nextLabel: 'Save my venue',
      onNext: create,
      body: <ReviewSummary values={v} onEdit={(i) => setStep(i)} />,
    },
    {
      id: 'photos',
      label: 'Photos',
      title: 'Now, show them the room.',
      lede: 'The first photo is the one artists see on browse. You need one before you can go live; up to 12 in all.',
      lockBack: true,
      optional: photos.length === 0,
      nextLabel: 'Continue',
      body:
        venue !== null ? (
          <VenuePhotoUploader
            venueId={venue.id}
            venueName={v.name}
            photos={photos}
            onUploaded={(p) => setPhotos((prev) => [...prev, p])}
          />
        ) : null,
    },
    {
      id: 'done',
      label: 'What’s left',
      title: identityApproved && hero !== undefined ? 'Ready for review.' : 'Your venue is saved.',
      lede: 'Here’s everything between you and going live.',
      lockBack: true,
      hideNext: true,
      body:
        venue !== null ? (
          <DoneScreen
            venueId={venue.id}
            hasHero={hero !== undefined}
            verificationState={verificationState}
            onAddPhotos={() => setStep(STEP_ORDER.indexOf('photos'))}
          />
        ) : null,
    },
  ];

  const journeySkip = billing.data?.stripe_live !== true && billing.data?.subscription == null ? (['subscribe'] as const) : [];

  return (
    <AppShell tone="form">
      <Stack gap="lg" py="md">
        <JourneyStrip
          journey="host"
          current={step >= steps.length - 1 ? (identityApproved ? 'review' : 'verify') : 'venue'}
          skip={[...journeySkip]}
        />
        <Wizard
          eyebrow="Venue setup"
          tone="tide"
          steps={steps}
          index={step}
          onIndexChange={setStep}
          isStepDone={(s, i) => i < step && (OPTIONAL_DONE[s.id]?.(v, photos.length) ?? true)}
        />
      </Stack>
    </AppShell>
  );
}

// Step ids in wizard order; the `steps` array in VenueNew follows this.
const STEP_ORDER = ['name', 'room', 'type', 'where', 'capacity', 'offer', 'extras', 'review', 'photos', 'done'] as const;

// Optional steps only tick in the step list when something was actually
// filled in; a skipped one stays unticked so it still reads as open.
const OPTIONAL_DONE: Record<string, (v: FormValues, photoCount: number) => boolean> = {
  offer: (v) =>
    v.sound_tech.length + v.sleeping_arrangements.length + v.food_offered.length +
      v.drink_offered.length + v.facilities.length > 0,
  extras: (v) =>
    v.house_rules.trim() !== '' ||
    v.stipend_offered ||
    v.travel_contribution_default ||
    v.equipment_default ||
    v.promotion_default ||
    v.recording_default,
  photos: (_v, photoCount) => photoCount > 0,
};

// Which fields each details step owns, in step order — used to send the host
// back to the right screen if the final check fails.
const DETAIL_STEP_FIELDS: (keyof FormValues)[][] = [
  ['name', 'slug'],
  ['summary', 'description'],
  ['venue_types', 'art_forms_accepted', 'vibe_tags'],
  ['address_line', 'city', 'region', 'postcode', 'country', 'lat', 'lng'],
  ['capacity_min', 'capacity_max'],
  [],
  ['stipend_description'],
];

function ReviewSummary({ values: v, onEdit }: { values: FormValues; onEdit: (step: number) => void }) {
  const rows: { label: string; value: ReactNode; step: number }[] = [
    { label: 'Name', value: v.name, step: 0 },
    { label: 'In one line', value: v.summary || '—', step: 1 },
    { label: 'The room', value: v.description, step: 1 },
    { label: 'Venue type', value: venueTypeNames(v.venue_types), step: 2 },
    { label: 'Art forms welcomed', value: artFormNames(v.art_forms_accepted), step: 2 },
    {
      label: 'Address',
      value: [v.address_line, v.city, v.region, v.postcode].filter((s) => s.length > 0).join(', '),
      step: 3,
    },
    { label: 'Crowd', value: `${v.capacity_min} to ${v.capacity_max} people`, step: 4 },
    {
      label: 'On offer',
      value:
        v.sound_tech.length +
          v.sleeping_arrangements.length +
          v.food_offered.length +
          v.drink_offered.length +
          v.facilities.length >
        0
          ? `${
              v.sound_tech.length +
              v.sleeping_arrangements.length +
              v.food_offered.length +
              v.drink_offered.length +
              v.facilities.length
            } things listed`
          : 'Nothing yet',
      step: 5,
    },
    {
      label: 'Stipend',
      value: v.stipend_offered ? 'Offered' : 'Not offered',
      step: 6,
    },
  ];
  return (
    <Paper withBorder radius="md" p={0}>
      {rows.map((r, i) => (
        <Group
          key={r.label}
          justify="space-between"
          wrap="nowrap"
          align="flex-start"
          gap="md"
          px="md"
          py="sm"
          style={{
            borderTop: i === 0 ? undefined : '1px solid var(--mantine-color-default-border)',
          }}
        >
          <Stack gap={0} style={{ minWidth: 0 }}>
            <Text size="xs" c="dimmed" fw={600}>
              {r.label}
            </Text>
            <Text size="sm" lineClamp={2}>
              {r.value}
            </Text>
          </Stack>
          <Anchor component="button" type="button" size="sm" onClick={() => onEdit(r.step)}>
            Edit
          </Anchor>
        </Group>
      ))}
    </Paper>
  );
}

function VenuePhotoUploader({
  venueId,
  venueName,
  photos,
  onUploaded,
}: {
  venueId: string;
  venueName: string;
  photos: UploadedPhoto[];
  onUploaded: (p: UploadedPhoto) => void;
}) {
  const [altText, setAltText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<string | null>(null);
  const upload = useMutation({
    mutationFn: async (files: File[]) => {
      const room = 12 - photos.length;
      const failures: string[] = [];
      for (const [i, file] of files.slice(0, room).entries()) {
        setProgress(`Uploading ${i + 1} of ${Math.min(files.length, room)}…`);
        // Alt text is required server-side. The host's description wins; the
        // fallback is at least specific to this venue, and editable later.
        const n = photos.length + i + 1;
        const alt = altText.trim().length > 0 ? altText.trim() : `${venueName}, photo ${n}`;
        try {
          const res = await uploadImage(`/api/venues/me/venues/${encodeURIComponent(venueId)}/photos`, file, {
            alt_text: alt,
          });
          const j = (await res.json()) as { photo?: UploadedPhoto };
          if (j.photo !== undefined) onUploaded(j.photo);
        } catch (err) {
          failures.push(`${file.name || 'A photo'}: ${err instanceof ApiError ? err.message : 'couldn’t be uploaded'}`);
        }
      }
      if (files.length > room) failures.push('Some photos were left out: a venue holds 12.');
      if (failures.length > 0) throw new Error(failures.join(' '));
    },
    onMutate: () => setError(null),
    onError: (err) => setError(err instanceof Error ? err.message : 'Some photos couldn’t be uploaded.'),
    onSettled: () => {
      setProgress(null);
      setAltText('');
    },
  });

  return (
    <Stack gap="md">
      {photos.length > 0 ? (
        <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="sm">
          {photos.map((p) => (
            <Card key={p.id} withBorder padding={0} radius="md">
              <Image src={p.url} alt={p.alt_text} h={130} fit="cover" />
              {p.is_hero ? (
                <Badge size="xs" pos="absolute" top={6} left={6} color="tide">
                  Browse photo
                </Badge>
              ) : null}
            </Card>
          ))}
        </SimpleGrid>
      ) : null}
      <TextInput
        label="Describe the photo"
        description="Optional, but it helps people who use screen readers. e.g. “The stage from the bar, fairy lights on”."
        value={altText}
        onChange={(e) => setAltText(e.currentTarget.value)}
        maxLength={280}
      />
      <Group gap="sm">
        <FileButton accept={PHOTO_ACCEPT} multiple onChange={(files) => files.length > 0 && upload.mutate(files)}>
          {(props) => (
            <Button
              {...props}
              data-autofocus
              loading={upload.isPending}
              disabled={photos.length >= 12}
              variant={photos.length > 0 ? 'light' : 'filled'}
              color="tide"
            >
              {photos.length >= 12 ? 'At the 12-photo limit' : photos.length > 0 ? 'Add more photos' : 'Upload photos'}
            </Button>
          )}
        </FileButton>
        <Text size="xs" c="dimmed">
          {progress ?? `${photos.length} of 12. Straight off your phone is fine.`}
        </Text>
      </Group>
      {error !== null ? (
        <Alert color="red" variant="light">
          {error}
        </Alert>
      ) : null}
    </Stack>
  );
}

function DoneScreen({
  venueId,
  hasHero,
  verificationState,
  onAddPhotos,
}: {
  venueId: string;
  hasHero: boolean;
  verificationState: 'approved' | 'pending' | 'rejected' | 'revoked' | 'none';
  onAddPhotos: () => void;
}) {
  const [submitted, setSubmitted] = useState(false);
  const submit = useMutation({
    mutationFn: () =>
      mutateJson(`/api/venues/me/venues/${encodeURIComponent(venueId)}/status`, { to: 'pending_review' }, { method: 'PATCH' }),
    onSuccess: () => setSubmitted(true),
  });
  const approved = verificationState === 'approved';

  const verificationDetail: Record<typeof verificationState, string> = {
    approved: 'Approved.',
    pending: 'With our team now. Most decisions land within 72 hours.',
    rejected: 'Your last submission needs another look. Re-submit and publishing unlocks once it’s approved.',
    revoked: 'Your verification was revoked. Re-submit to restore publishing.',
    none: 'A government ID and a photo of you holding it. Most reviews land within 72 hours.',
  };

  const items: ChecklistItem[] = [
    { id: 'details', label: 'Venue details', detail: 'Saved as a draft.', done: true },
    {
      id: 'photo',
      label: 'A browse photo',
      detail: 'Needed to go live.',
      done: hasHero,
      action: (
        <Button size="compact-sm" variant="light" color="tide" onClick={onAddPhotos}>
          Add photos
        </Button>
      ),
    },
    {
      id: 'verify',
      label: 'Verify your identity',
      detail: verificationDetail[verificationState],
      done: approved,
      action:
        verificationState === 'pending' ? undefined : (
          <Link to="/verification" style={{ textDecoration: 'none' }}>
            <Button component="span" size="compact-sm" variant="light" color="tide">
              {verificationState === 'none' ? 'Verify now' : 'Re-submit'}
            </Button>
          </Link>
        ),
    },
    {
      id: 'review',
      label: 'Submit for review',
      detail: submitted
        ? 'Sent. We’ll let you know either way, usually within 72 hours.'
        : 'Our team checks every venue before it goes live, usually within 72 hours.',
      done: submitted,
    },
    {
      id: 'calendar',
      label: 'Open your calendar',
      detail: 'Optional: mark the dates you’re happy to host from the venue editor.',
      done: false,
      action: (
        <Link to="/host/venues/$id/edit" params={{ id: venueId }} style={{ textDecoration: 'none' }}>
          <Button component="span" size="compact-sm" variant="light" color="tide">
            Open editor
          </Button>
        </Link>
      ),
    },
  ];

  return (
    <Stack gap="lg">
      <NextStepsChecklist items={items} />
      {submit.isError ? (
        <Alert color="red" variant="light">
          {submit.error instanceof Error ? submit.error.message : 'We couldn’t submit it. Try again.'}
        </Alert>
      ) : null}
      <Group gap="sm">
        {!submitted ? (
          <Button
            size="md"
            variant="gradient"
            gradient={{ from: 'tide.6', to: 'tide.4', deg: 35 }}
            disabled={!approved || !hasHero}
            loading={submit.isPending}
            onClick={() => submit.mutate()}
          >
            Submit for review
          </Button>
        ) : null}
        <Link to="/host/venues/$id/edit" params={{ id: venueId }} style={{ textDecoration: 'none' }}>
          <Button component="span" size="md" variant="light" color="tide">
            Open the venue editor
          </Button>
        </Link>
        <Link to="/host/dashboard" style={{ textDecoration: 'none' }}>
          <Button component="span" size="md" variant="subtle" color="tide">
            Go to my dashboard
          </Button>
        </Link>
      </Group>
      {!submitted && (!approved || !hasHero) ? (
        <Text size="xs" c="dimmed">
          Submit for review unlocks once you have a browse photo and your identity is verified. Your
          draft is saved; pick this up from the venue editor any time.
        </Text>
      ) : null}
    </Stack>
  );
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 80);
}

function toCreatePayload(v: FormValues): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    name: v.name.trim(),
    slug: v.slug,
    description: v.description.trim(),
    venue_types: v.venue_types,
    art_forms_accepted: v.art_forms_accepted,
    capacity_min: v.capacity_min,
    capacity_max: v.capacity_max,
    address_line: v.address_line,
    city: v.city,
    country: v.country,
    lat: v.lat === '' ? 0 : v.lat,
    lng: v.lng === '' ? 0 : v.lng,
    address_visible_to_public: v.address_visible_to_public,
    timezone: v.timezone,
    stipend_offered: v.stipend_offered,
    travel_contribution_default: v.travel_contribution_default,
    equipment_default: v.equipment_default,
    promotion_default: v.promotion_default,
    recording_default: v.recording_default,
  };
  if (v.summary.length > 0) payload['summary'] = v.summary.trim();
  if (v.region.length > 0) payload['region'] = v.region;
  if (v.postcode.length > 0) payload['postcode'] = v.postcode;
  if (v.usual_event_times.length > 0) payload['usual_event_times'] = v.usual_event_times;
  if (v.vibe_tags.length > 0) payload['vibe_tags'] = v.vibe_tags;
  if (v.sound_tech.length > 0) payload['sound_tech'] = v.sound_tech;
  if (v.sleeping_arrangements.length > 0) payload['sleeping_arrangements'] = v.sleeping_arrangements;
  if (v.food_offered.length > 0) payload['food_offered'] = v.food_offered;
  if (v.drink_offered.length > 0) payload['drink_offered'] = v.drink_offered;
  if (v.facilities.length > 0) payload['facilities'] = v.facilities;
  if (v.house_rules.length > 0) payload['house_rules'] = v.house_rules;
  if (v.stipend_description.length > 0) payload['stipend_description'] = v.stipend_description;
  if (v.travel_contribution_description.length > 0)
    payload['travel_contribution_description'] = v.travel_contribution_description;
  if (v.equipment_description.length > 0) payload['equipment_description'] = v.equipment_description;
  if (v.promotion_description.length > 0) payload['promotion_description'] = v.promotion_description;
  if (v.recording_description.length > 0) payload['recording_description'] = v.recording_description;
  return payload;
}

export const Route = createFileRoute('/host/venues/new')({
  component: VenueNew,
});
